import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../utils/auth';

const UserCenter = () => {
  const { currentUser } = useAuth();
  const { t } = useTranslation();

  const [activeTab, setActiveTab] = useState('profile');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [profileForm, setProfileForm] = useState({
    name: currentUser?.name || '',
    title: currentUser?.title || '',
    specialty: currentUser?.specialty || '',
    license_number: currentUser?.license_number || ''
  });
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const handleProfileUpdate = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError('');
    setMessage('');

    try {
      const response = await fetch('/api/v1/doctors/profile', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('doctor_token')}`
        },
        body: JSON.stringify(profileForm)
      });

      if (!response.ok) {
        throw new Error(t('userCenter.updateFailed'));
      }

      setMessage(t('userCenter.profileUpdateSuccess'));
    } catch (requestError) {
      setError(`${t('userCenter.updateFailed')}：${requestError.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordUpdate = async (event) => {
    event.preventDefault();

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setError(t('userCenter.passwordMismatch'));
      return;
    }

    if (passwordForm.newPassword.length < 6) {
      setError(t('userCenter.passwordMinLength'));
      return;
    }

    setLoading(true);
    setError('');
    setMessage('');

    try {
      const response = await fetch('/api/v1/doctors/change-password', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('doctor_token')}`
        },
        body: JSON.stringify({
          currentPassword: passwordForm.currentPassword,
          newPassword: passwordForm.newPassword
        })
      });

      if (!response.ok) {
        throw new Error(t('userCenter.passwordUpdateFailed'));
      }

      setMessage(t('userCenter.passwordUpdateSuccess'));
      setPasswordForm({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
    } catch (requestError) {
      setError(`${t('userCenter.passwordUpdateFailed')}：${requestError.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="stack">
      <div className="page-header">
        <div className="page-title-block">
          <h1 className="page-title">{t('dashboard.userCenter')}</h1>
        </div>
      </div>

      {message && (
        <div className="status-banner status-banner--success">
          <span>{message}</span>
        </div>
      )}

      {error && (
        <div className="status-banner status-banner--error">
          <span>{error}</span>
        </div>
      )}

      <div className="utility-grid">
        <section className="surface-panel surface-panel--soft">
          <div className="surface-panel__body stack">
            <div className="page-title-block">
              <h2 className="report-panel-title">{t('dashboard.doctorInfo')}</h2>
            </div>

            <div className="info-list">
              <div className="info-list__row">
                <span className="info-list__label">{t('userCenter.name')}</span>
                <span className="info-list__value">{profileForm.name || t('dashboard.notSet')}</span>
              </div>
              <div className="info-list__row">
                <span className="info-list__label">{t('userCenter.title')}</span>
                <span className="info-list__value">{profileForm.title || t('dashboard.notSet')}</span>
              </div>
              <div className="info-list__row">
                <span className="info-list__label">{t('userCenter.specialty')}</span>
                <span className="info-list__value">{profileForm.specialty || t('dashboard.notSet')}</span>
              </div>
              <div className="info-list__row">
                <span className="info-list__label">{t('userCenter.licenseNumber')}</span>
                <span className="info-list__value">{profileForm.license_number || t('dashboard.notSet')}</span>
              </div>
            </div>

          </div>
        </section>

        <section className="surface-panel">
          <div className="surface-panel__body stack">
            <div className="tab-switcher">
              <button
                type="button"
                className={`tab-switcher__button ${activeTab === 'profile' ? 'is-active' : ''}`}
                onClick={() => setActiveTab('profile')}
              >
                {t('userCenter.personalInfo')}
              </button>
              <button
                type="button"
                className={`tab-switcher__button ${activeTab === 'password' ? 'is-active' : ''}`}
                onClick={() => setActiveTab('password')}
              >
                {t('userCenter.changePassword')}
              </button>
            </div>

            {activeTab === 'profile' && (
              <form onSubmit={handleProfileUpdate} className="stack">
                <div className="form-grid">
                  <div className="field">
                    <label htmlFor="profile-name" className="field__label">{t('userCenter.name')}</label>
                    <input
                      id="profile-name"
                      className="theme-input"
                      type="text"
                      value={profileForm.name}
                      onChange={(event) => setProfileForm({ ...profileForm, name: event.target.value })}
                    />
                  </div>

                  <div className="field">
                    <label htmlFor="profile-title" className="field__label">{t('userCenter.title')}</label>
                    <input
                      id="profile-title"
                      className="theme-input"
                      type="text"
                      value={profileForm.title}
                      onChange={(event) => setProfileForm({ ...profileForm, title: event.target.value })}
                    />
                  </div>

                  <div className="field">
                    <label htmlFor="profile-specialty" className="field__label">{t('userCenter.specialty')}</label>
                    <input
                      id="profile-specialty"
                      className="theme-input"
                      type="text"
                      value={profileForm.specialty}
                      onChange={(event) => setProfileForm({ ...profileForm, specialty: event.target.value })}
                    />
                  </div>

                  <div className="field">
                    <label htmlFor="profile-license" className="field__label">{t('userCenter.licenseNumber')}</label>
                    <input
                      id="profile-license"
                      className="theme-input"
                      type="text"
                      value={profileForm.license_number}
                      onChange={(event) => setProfileForm({ ...profileForm, license_number: event.target.value })}
                    />
                  </div>
                </div>

                <button type="submit" className="theme-button theme-button--success" disabled={loading}>
                  {loading ? t('userCenter.updating') : t('userCenter.updateProfile')}
                </button>
              </form>
            )}

            {activeTab === 'password' && (
              <form onSubmit={handlePasswordUpdate} className="stack">
                <div className="form-grid form-grid--single">
                  <div className="field">
                    <label htmlFor="current-password" className="field__label">{t('userCenter.currentPassword')}</label>
                    <input
                      id="current-password"
                      className="theme-input"
                      type="password"
                      value={passwordForm.currentPassword}
                      onChange={(event) => setPasswordForm({ ...passwordForm, currentPassword: event.target.value })}
                      required
                    />
                  </div>

                  <div className="field">
                    <label htmlFor="new-password" className="field__label">{t('userCenter.newPassword')}</label>
                    <input
                      id="new-password"
                      className="theme-input"
                      type="password"
                      value={passwordForm.newPassword}
                      onChange={(event) => setPasswordForm({ ...passwordForm, newPassword: event.target.value })}
                      minLength="6"
                      required
                    />
                  </div>

                  <div className="field">
                    <label htmlFor="confirm-password" className="field__label">{t('userCenter.confirmPassword')}</label>
                    <input
                      id="confirm-password"
                      className="theme-input"
                      type="password"
                      value={passwordForm.confirmPassword}
                      onChange={(event) => setPasswordForm({ ...passwordForm, confirmPassword: event.target.value })}
                      required
                    />
                  </div>
                </div>

                <button type="submit" className="theme-button theme-button--primary" disabled={loading}>
                  {loading ? t('userCenter.updating') : t('userCenter.changePassword')}
                </button>
              </form>
            )}
          </div>
        </section>
      </div>
    </div>
  );
};

export default UserCenter;
